import type { Project } from '@finsim/model';
import { useUnit } from 'effector-react';
import { FileDown, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { $effectiveProject, $hasOverrides } from '../../entities/item-visibility';
import { $mcRunning, $result } from '../../entities/simulation';
import { $simMode, simModeChanged } from '../../entities/view-settings';
import { formatPercent } from '../../shared/lib/format';
import { useHotkeys } from '../../shared/lib/hotkeys';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { LOCALES } from '../../shared/lib/locale';
import {
  Button,
  Dialog,
  DialogContent,
  Field,
  IconButton,
  Segmented,
  Select,
  Tooltip,
} from '../../shared/ui';
import { scenarioColor } from '../chart-canvas';
import { REPORT_SECTIONS, type ReportOptions, type ReportSectionKey } from './lib/options';
import {
  $mcForReport,
  $options,
  $phase,
  $progress,
  $reportError,
  $reportOpen,
  exportCancelled,
  exportRequested,
  optionsChanged,
  reportDialogClosed,
  reportDialogOpened,
  scenarioToggled,
  sectionToggled,
} from './model';

const LANGUAGE_NAME = { en: 'English', bg: 'Български' } as const;

/**
 * Top-bar button that opens the report dialog. With a label from 1600 px; icon-only below that
 * (and in the compact viewer), where the bar has no room for another label.
 */
export function ReportButton({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const open = useUnit(reportDialogOpened);
  const icon = (
    <Tooltip content={t('report.dialog.hint')} shortcut={['mod', 'P']}>
      <IconButton
        label={t('report.dialog.button')}
        onClick={open}
        className={compact ? undefined : 'min-[1600px]:hidden'}
      >
        <FileDown />
      </IconButton>
    </Tooltip>
  );
  if (compact) return icon;
  return (
    <>
      {icon}
      <Tooltip content={t('report.dialog.hint')} shortcut={['mod', 'P']}>
        <Button
          size="sm"
          variant="ghost"
          leadingIcon={<FileDown />}
          onClick={open}
          className="hidden min-[1600px]:inline-flex"
        >
          {t('report.dialog.button')}
        </Button>
      </Tooltip>
    </>
  );
}

/** The export dialog (mounted once in the app); ⌘P / Ctrl+P opens it on a project. */
export function ReportDialog() {
  const { t } = useTranslation();
  const [open, close, openDialog, project, result, phase] = useUnit([
    $reportOpen,
    reportDialogClosed,
    reportDialogOpened,
    $effectiveProject,
    $result,
    $phase,
  ]);
  useHotkeys({ 'mod+p': project && result ? openDialog : undefined });
  const busy = phase !== 'idle';
  return (
    <Dialog
      open={open && project !== null}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      {project && (
        <DialogContent
          title={t('report.dialog.title')}
          description={t('report.dialog.description')}
          closeLabel={t('common.close')}
          footer={busy ? <Progress /> : <Actions />}
        >
          <ReportForm project={project} />
        </DialogContent>
      )}
    </Dialog>
  );
}

function ReportForm({ project }: { project: Project }) {
  const { t } = useTranslation();
  const [options, change, toggleSection, toggleScenario, overrides, phase] = useUnit([
    $options,
    optionsChanged,
    sectionToggled,
    scenarioToggled,
    $hasOverrides,
    $phase,
  ]);
  if (!options) return null;
  const busy = phase !== 'idle';
  const chosen = new Set<string>(options.scenarioIds);

  return (
    <fieldset disabled={busy} className="space-y-4">
      <div role="group" aria-labelledby="report-variants" className="space-y-1">
        <h3 id="report-variants" className="text-xs text-text-3">
          {t('report.dialog.variants')}
        </h3>
        <ul className="grid gap-0.5 sm:grid-cols-2">
          {project.scenarios.map((s) => (
            <li key={s.id}>
              <Check
                checked={chosen.has(s.id)}
                onChange={(on) => {
                  toggleScenario({ id: s.id, on });
                }}
              >
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: scenarioColor(s) }}
                />
                <span className="truncate">{s.name}</span>
              </Check>
            </li>
          ))}
        </ul>
        {chosen.size === 0 && (
          <p className="text-xs text-negative">{t('report.dialog.noVariants')}</p>
        )}
      </div>

      <Sections
        options={options}
        onToggle={(key, on) => {
          toggleSection({ key, on });
        }}
      />

      <div className="space-y-3">
        <Field label={t('report.dialog.values')}>
          {() => (
            <Segmented
              label={t('report.dialog.values')}
              value={options.valueMode}
              options={[
                {
                  value: 'nominal',
                  label: t('valueMode.nominal'),
                  hint: t('valueMode.nominalHint'),
                },
                { value: 'real', label: t('valueMode.real'), hint: t('valueMode.realHint') },
              ]}
              onValueChange={(valueMode) => {
                change({ valueMode });
              }}
            />
          )}
        </Field>
        <Field label={t('report.dialog.language')}>
          {({ id }) => (
            <Select
              id={id}
              value={options.locale}
              options={LOCALES.map((l) => ({ value: l, label: LANGUAGE_NAME[l] }))}
              onValueChange={(locale) => {
                change({ locale });
              }}
            />
          )}
        </Field>
        <Field label={t('report.dialog.pageSize')}>
          {() => (
            <Segmented
              label={t('report.dialog.pageSize')}
              value={options.pageSize}
              options={[
                { value: 'A4', label: 'A4' },
                { value: 'LETTER', label: t('report.dialog.letter') },
              ]}
              onValueChange={(pageSize) => {
                change({ pageSize });
              }}
            />
          )}
        </Field>
      </div>

      {overrides && <p className="text-xs text-text-3">{t('report.dialog.overrides')}</p>}
    </fieldset>
  );
}

function Sections({
  options,
  onToggle,
}: {
  options: ReportOptions;
  onToggle: (key: ReportSectionKey, on: boolean) => void;
}) {
  const { t } = useTranslation();
  const [mc, mcRunning, simMode, setSimMode] = useUnit([
    $mcForReport,
    $mcRunning,
    $simMode,
    simModeChanged,
  ]);
  const notes: Partial<Record<ReportSectionKey, ReactNode>> = {};
  const unavailable = new Set<ReportSectionKey>();
  if (options.scenarioIds.length < 2) {
    unavailable.add('comparison');
    notes.comparison = t('report.dialog.comparisonNeedsTwo');
  }
  if (!mc) {
    unavailable.add('risk');
    notes.risk =
      simMode === 'monteCarlo' || mcRunning ? (
        <span className="inline-flex items-center gap-1">
          <Loader2 className="size-3 animate-spin" aria-hidden />
          {t('report.dialog.riskRunning')}
        </span>
      ) : (
        <>
          {t('report.dialog.riskNeedsMc')}{' '}
          <button
            type="button"
            className="text-accent underline-offset-2 hover:underline"
            onClick={() => {
              setSimMode('monteCarlo');
            }}
          >
            {t('report.dialog.runMc')}
          </button>
        </>
      );
  }
  return (
    <div role="group" aria-labelledby="report-sections" className="space-y-1">
      <h3 id="report-sections" className="text-xs text-text-3">
        {t('report.dialog.sections')}
      </h3>
      <ul className="space-y-0.5">
        {REPORT_SECTIONS.map((key) => (
          <li key={key}>
            <Check
              checked={options.sections[key] && !unavailable.has(key)}
              disabled={unavailable.has(key)}
              onChange={(on) => {
                onToggle(key, on);
              }}
            >
              <span>{t(`report.dialog.sectionNames.${key}`)}</span>
            </Check>
            {notes[key] && <p className="pb-1 pl-8 text-xs text-text-3">{notes[key]}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Check({
  checked,
  disabled = false,
  onChange,
  children,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-bg-3 has-disabled:text-text-3 has-disabled:hover:bg-transparent">
      <input
        type="checkbox"
        className="size-4 shrink-0 accent-[var(--accent)]"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      {children}
    </label>
  );
}

function Actions() {
  const { t, i18n } = useTranslation();
  const [options, error, close, request] = useUnit([
    $options,
    $reportError,
    reportDialogClosed,
    exportRequested,
  ]);
  return (
    <div className="flex w-full items-center gap-2">
      {error && (
        <p role="alert" className="min-w-0 flex-1 text-xs text-negative">
          {t('report.dialog.failed')}
        </p>
      )}
      <div className="flex-1" />
      <Button onClick={close}>{t('common.cancel')}</Button>
      <Button
        variant="primary"
        leadingIcon={<FileDown />}
        disabled={!options || options.scenarioIds.length === 0}
        onClick={() => {
          request({ translate: (locale) => i18n.getFixedT(locale) });
        }}
      >
        {t('report.dialog.export')}
      </Button>
    </div>
  );
}

function Progress() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [phase, progress, cancel] = useUnit([$phase, $progress, exportCancelled]);
  const label =
    phase === 'preparing'
      ? t('report.dialog.preparing')
      : t('report.dialog.rendering', { percent: formatPercent(progress, locale, 0) });
  return (
    <div className="flex w-full items-center gap-3">
      <span
        role="progressbar"
        aria-label={t('report.dialog.progress')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        className="h-1.5 w-32 overflow-hidden rounded-full bg-bg-4"
      >
        <span
          className="block h-full rounded-full bg-accent transition-[width] duration-150"
          style={{ width: `${String(Math.max(4, progress * 100))}%` }}
        />
      </span>
      <span role="status" className="tabular min-w-0 flex-1 truncate text-xs text-text-2">
        {label}
      </span>
      <Button onClick={cancel}>{t('report.dialog.cancelExport')}</Button>
    </div>
  );
}
